import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginTemplate } from '@/lib/loginData';
import { cn } from '@/lib/utils';
import { ArrowRight, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ShareDialog } from './ShareDialog';

interface LoginCardProps {
  template: LoginTemplate;
  index: number;
}

const LoginCard: React.FC<LoginCardProps> = ({ template, index }) => {
  const navigate = useNavigate();
  const Icon = template.icon;
  const isDark = ['dota2', 'mobilelegends', 'discord', 'steam'].includes(template.id);
  
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking the share button
    if ((e.target as HTMLElement).closest('[data-share-button]')) {
      return;
    }
    navigate(`/login/${template.id}`);
  };

  return (
    <div 
      onClick={handleCardClick}
      className={cn(
        "group relative overflow-hidden rounded-xl border p-6 shadow-subtle transition-all duration-300 cursor-pointer",
        "hover:shadow-elevation hover:-translate-y-1",
        isDark && "text-white"
      )}
      style={{ 
        animationDelay: `${index * 50}ms`,
        backgroundColor: isDark ? template.backgroundColor : undefined,
        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : undefined,
      }}
    >
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
        style={{ 
          background: `linear-gradient(135deg, ${template.primaryColor}, ${template.secondaryColor})`
        }}
      />
      
      <div className="relative flex flex-col h-full">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex items-center h-12">
              {template.logoUrl ? (
                <img 
                  src={template.logoUrl} 
                  alt={template.name} 
                  className={cn(
                    "object-contain",
                    template.id === 'facebook' ? "h-8" : 
                    template.id === 'instagram' ? "h-10" : 
                    template.id === 'github' ? "h-9 w-9" : 
                    template.id === 'linkedin' ? "h-5" : 
                    template.id === 'reddit' ? "h-10" : 
                    template.id === 'discord' ? "h-9 w-9" : 
                    template.id === 'steam' ? "h-7" : 
                    "h-8"
                  )}
                />
              ) : (
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                  style={{ backgroundColor: template.primaryColor }}
                >
                  <Icon className="w-6 h-6" />
                </div>
              )}
            </div>
            
            <h3 className={cn(
              "text-lg font-medium mt-2",
              isDark ? "text-white" : "text-foreground"
            )}>{template.name}</h3>
            
            <p className={cn(
              "text-sm line-clamp-2",
              isDark ? "text-gray-300" : "text-muted-foreground"
            )}>{template.description}</p>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <span className={cn(
            "text-muted-foreground group-hover:text-primary transition-colors duration-300",
            isDark && "text-gray-400 group-hover:text-white"
          )}>
            <ArrowRight className="w-5 h-5" />
          </span>

          <ShareDialog template={template}>
            <Button 
              variant="ghost" 
              size="icon" 
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              data-share-button
              onClick={(e) => e.stopPropagation()}
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </ShareDialog>
        </div>
      </div>
    </div>
  );
};

export default LoginCard;